import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getStoreStrings, HUAWEI_URL } from '@/utils/storeRedirectStrings';

/**
 * /huawei — Immediately redirects visitors to the Falak AppGallery listing.
 * Shows a graceful fallback UI in case the auto-redirect is blocked
 * (some in-app browsers strip window.location assignments).
 */
const HuaweiRedirect = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const isRTL = lang === 'ar' || lang === 'ur' || lang === 'fa';
  const t = getStoreStrings(lang);

  useEffect(() => {
    // Kick off the redirect on the next tick to give the fallback a chance
    // to paint if the browser blocks the navigation.
    const timer = setTimeout(() => {
      window.location.replace(HUAWEI_URL);
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at 50% 0%, #14213D 0%, #050914 60%, #000 100%)',
        color: '#E5E7EB',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 20px',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", Tahoma, sans-serif',
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="huawei-redirect-page"
    >
      <div style={{ maxWidth: 460, textAlign: 'center' }}>
        {/* Crescent logo */}
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            margin: '0 auto 24px',
            position: 'relative',
            background: 'radial-gradient(circle at 35% 35%, #FDE68A 0%, #F59E0B 40%, #1F2937 70%)',
            boxShadow: '0 0 40px rgba(253, 230, 138, 0.35)',
          }}
        />

        <h1 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 12px', letterSpacing: 0.3 }}>
          Falak
        </h1>

        <p style={{ color: '#94A3B8', fontSize: 15, margin: '0 0 32px', lineHeight: 1.55 }}>
          {t.redirecting}
        </p>

        {/* Loading dots */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 40 }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#F59E0B',
                opacity: 0.6,
                animation: `falak-dot 1.2s ${i * 0.15}s infinite ease-in-out`,
              }}
            />
          ))}
        </div>

        <p style={{ color: '#64748B', fontSize: 13, margin: '0 0 16px' }}>
          {t.openManually}
        </p>

        <a
          href={HUAWEI_URL}
          style={{
            display: 'inline-block',
            padding: '12px 28px',
            borderRadius: 999,
            background: '#F59E0B',
            color: '#0B132B',
            fontWeight: 600,
            fontSize: 15,
            textDecoration: 'none',
            letterSpacing: 0.3,
          }}
          data-testid="huawei-open-button"
        >
          {t.openHuawei}
        </a>
      </div>

      <style>{`
        @keyframes falak-dot {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default HuaweiRedirect;
